import { type ReactNode } from 'react';
import { 
  FormProvider as RHFFormProvider, 
  type UseFormReturn, 
  type FieldValues,
  type SubmitHandler,
} from 'react-hook-form';
import {
  Box,
  Stack,
  Card,
  CardContent,
  CardActions,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { AppButton } from '../design-system';
import Loading from '../ui/Loading';

interface FormProviderProps<T extends FieldValues> {
  methods: UseFormReturn<T>;
  onSubmit: SubmitHandler<T>;
  children: ReactNode;
  title?: string;
  subtitle?: string;
  loading?: boolean;
  submitText?: string;
  cancelText?: string;
  showCancel?: boolean;
  onCancel?: () => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  variant?: 'card' | 'plain';
}

function FormProvider<T extends FieldValues>({
  methods,
  onSubmit,
  children,
  title,
  subtitle,
  loading = false,
  submitText = 'Save',
  cancelText = 'Cancel',
  showCancel = true,
  onCancel,
  disabled = false,
  sx,
  variant = 'card',
}: FormProviderProps<T>) {
  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = methods;

  const isFormDisabled = disabled || loading || isSubmitting;

  const formContent = (
    <RHFFormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={sx}>
        {variant === 'card' && (title || subtitle) && (
          <Box sx={{ mb: 3 }}>
            {title && (
              <Typography variant="h5" component="h1" gutterBottom>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        <Stack spacing={3}>
          {loading ? (
            <Loading variant="circular" message="Loading form..." />
          ) : (
            children
          )}
        </Stack>

        {variant === 'card' && (
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {showCancel && (
              <AppButton
                variant="outlined"
                size="medium"
                onClick={onCancel}
                disabled={isFormDisabled}
                startIcon={<Cancel />}
              >
                {cancelText}
              </AppButton>
            )}
            <AppButton
              type="submit"
              variant="contained"
              size="medium"
              disabled={isFormDisabled || !isDirty}
              startIcon={<Save />}
            >
              {isSubmitting ? 'Saving...' : submitText}
            </AppButton>
          </Box>
        )}
      </Box>
    </RHFFormProvider>
  );

  if (variant === 'card') {
    return (
      <Card>
        <CardContent>
          {title && (
            <Typography variant="h6" component="h2" gutterBottom>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {subtitle}
            </Typography>
          )}
          
          <RHFFormProvider {...methods}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                {loading ? (
                  <Loading variant="circular" message="Loading form..." />
                ) : (
                  children
                )}
              </Stack>
            </Box>
          </RHFFormProvider>
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
          {showCancel && (
            <AppButton
              variant="outlined"
              size="medium"
              onClick={onCancel}
              disabled={isFormDisabled}
              startIcon={<Cancel />}
            >
              {cancelText}
            </AppButton>
          )}
          <AppButton
            type="submit"
            variant="contained"
            size="medium"
            disabled={isFormDisabled || !isDirty}
            startIcon={<Save />}
            onClick={handleSubmit(onSubmit)}
          >
            {isSubmitting ? 'Saving...' : submitText}
          </AppButton>
        </CardActions>
      </Card>
    );
  }

  return formContent;
}

export default FormProvider;